import React from "react";

const C = {
  navy: "#121531",
  navyLight: "#1c2045",
  cyan: "#42A4D9",
  gold: "#D4A843",
  white: "#FFFFFF",
  offWhite: "#F4F6FA",
  muted: "#8892A4",
  border: "rgba(66,164,217,0.18)",
};

const STATUS_COLORS = {
  active:   { bg: "rgba(66,217,130,0.1)",  border: "rgba(66,217,130,0.3)",  dot: "#42D982", label: "Active" },
  draft:    { bg: "rgba(212,168,67,0.1)",  border: "rgba(212,168,67,0.3)",  dot: "#D4A843", label: "Draft" },
  archived: { bg: "rgba(136,146,164,0.1)", border: "rgba(136,146,164,0.2)", dot: "#8892A4", label: "Archived" },
};

export default function Home({ decks, navigate }) {
  const active   = decks.filter(d => d.status === "active");
  const draft    = decks.filter(d => d.status === "draft");
  const archived = decks.filter(d => d.status === "archived");

  return (
    <div style={{
      minHeight: "100vh",
      background: C.navy,
      fontFamily: "'Georgia', serif",
      color: C.white,
    }}>
      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${C.border}`,
        padding: "0 48px",
        height: 64,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "rgba(18,21,49,0.95)",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.cyan }} />
          <span style={{ fontSize: 13, color: C.cyan, letterSpacing: "0.12em" }}>
            CATALYST MEDICAL EDUCATION
          </span>
          <span style={{ color: C.border, margin: "0 8px" }}>|</span>
          <span style={{ fontSize: 13, color: C.muted, letterSpacing: "0.08em" }}>
            Interactive Portal
          </span>
        </div>
        <div style={{ fontSize: 11, color: C.muted }}>
          interactive.catalystmeded.com
        </div>
      </div>

      {/* Hero */}
      <div style={{
        padding: "64px 48px 48px",
        maxWidth: 1100,
        margin: "0 auto",
      }}>
        <div style={{
          fontSize: 11,
          color: C.gold,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          marginBottom: 16,
        }}>
          Interactive Decks
        </div>
        <h1 style={{
          fontSize: 40,
          fontWeight: 400,
          color: C.white,
          margin: 0,
          lineHeight: 1.2,
        }}>
          Proposals, Planners &<br />
          <span style={{ color: C.cyan }}>Educational Initiatives</span>
        </h1>
        <div style={{ height: 2, width: 64, background: `linear-gradient(90deg, ${C.cyan}, ${C.gold})`, borderRadius: 2, margin: "24px 0" }} />
        <p style={{ fontSize: 14, color: C.muted, maxWidth: 560, lineHeight: 1.7, margin: 0 }}>
          Interactive grant proposals, combined initiative decks, and educational planning tools — built for funders, medical education planners, and the Catalyst team.
        </p>
      </div>

      {/* Deck grid */}
      <div style={{ padding: "0 48px 80px", maxWidth: 1100, margin: "0 auto" }}>

        {active.length > 0 && (
          <Section label="Active" decks={active} navigate={navigate} />
        )}
        {draft.length > 0 && (
          <Section label="Drafts" decks={draft} navigate={navigate} />
        )}
        {archived.length > 0 && (
          <Section label="Archived" decks={archived} navigate={navigate} />
        )}

        {/* Coming soon / add new callout */}
        <div style={{
          marginTop: 48,
          border: `1px dashed rgba(66,164,217,0.2)`,
          borderRadius: 12,
          padding: "32px",
          display: "flex",
          alignItems: "center",
          gap: 24,
          background: "rgba(66,164,217,0.02)",
        }}>
          <div style={{
            width: 48, height: 48,
            borderRadius: 10,
            border: `1px dashed ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, color: C.muted, flexShrink: 0,
          }}>+</div>
          <div>
            <div style={{ fontSize: 14, color: C.offWhite, marginBottom: 4 }}>New deck coming soon</div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              Each new proposal or initiative is added here automatically. Share this page with your team as the single home for all interactive Catalyst content.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ label, decks, navigate }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{
        fontSize: 11,
        color: C.muted,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}>
        <span>{label}</span>
        <div style={{ flex: 1, height: 1, background: C.border }} />
        <span>{decks.length}</span>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: 16,
      }}>
        {decks.map(deck => (
          <DeckCard key={deck.slug} deck={deck} navigate={navigate} />
        ))}
      </div>
    </div>
  );
}

function DeckCard({ deck, navigate }) {
  const [hover, setHover] = React.useState(false);
  const status = STATUS_COLORS[deck.status] || STATUS_COLORS.active;

  return (
    <div
      onClick={() => navigate(deck.slug)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.025)",
        border: `1px solid ${hover ? "rgba(66,164,217,0.35)" : C.border}`,
        borderRadius: 12,
        padding: "24px 24px",
        cursor: "pointer",
        transition: "all 0.18s",
        transform: hover ? "translateY(-2px)" : "none",
      }}
    >
      {/* Top row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div style={{
          fontSize: 11,
          padding: "3px 10px",
          borderRadius: 20,
          background: status.bg,
          border: `1px solid ${status.border}`,
          color: status.dot,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: status.dot }} />
          {status.label}
        </div>
        <div style={{ fontSize: 11, color: C.muted }}>{deck.year}</div>
      </div>

      {/* Title */}
      <div style={{ fontFamily: "'Georgia', serif", fontSize: 17, color: C.white, marginBottom: 6, lineHeight: 1.3 }}>
        {deck.funder}
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
        {deck.type} · {deck.ta}
      </div>

      {/* Footer */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderTop: `1px solid ${C.border}`,
        paddingTop: 14,
      }}>
        <div style={{ fontSize: 11, color: C.muted }}>
          Created {deck.created}
        </div>
        <div style={{
          fontSize: 12,
          color: hover ? C.cyan : C.muted,
          transition: "color 0.18s",
        }}>
          Open →
        </div>
      </div>
    </div>
  );
}
